import { useEffect, useRef, memo } from 'react';
import { Map } from 'maplibre-gl';
import { MaplibreMeasureControl as MeasureControlLibrary } from '@watergis/maplibre-gl-terradraw';
import '@watergis/maplibre-gl-terradraw/dist/maplibre-gl-terradraw.css';
import { TerraDrawPointMode, TerraDrawLineStringMode, TerraDrawPolygonMode } from 'terra-draw';

interface MeasureControlLibraryProps {
  map: Map | null;
  isActive: boolean;
}

const DrawControl = memo(({ map, isActive }: MeasureControlLibraryProps) => {
  const drawControlRef = useRef<MeasureControlLibrary | null>(null);

  useEffect(() => {
    if (map && isActive) {
      if (!drawControlRef.current) { // Aggiungi solo se non esiste già un'istanza
        const newDrawControlInstance = new MeasureControlLibrary({
          modes: ['render', 'select', 'delete-selection', 'delete', 'rectangle', 'angled-rectangle', 'polygon', 'linestring', 'circle', 'point', 'freehand', 'sensor', 'sector', 'download'],
          open: false,
          //computeElevation: true,
          modeOptions: {
            point: new TerraDrawPointMode({
              styles: {
                pointColor: '#0000FF', // Stile semplificato Blu
                pointWidth: 5, // Stile semplificato
                pointOutlineColor: '#0000FF', // Stile semplificato Blu
                pointOutlineWidth: 1
              }
            }),
            linestring: new TerraDrawLineStringMode({
              styles: {
                lineStringColor: '#00FF00', // Stile semplificato Verde
                lineStringWidth: 5, // Stile semplificato
                closingPointColor: '#FFFFFF',
                closingPointWidth: 3,
                closingPointOutlineColor: '#00FF00', // Stile semplificato Verde
                closingPointOutlineWidth: 1
              }
            }),
            polygon: new TerraDrawPolygonMode({
              styles: {
                fillColor: '#FFFF00', // Stile semplificato Giallo
                fillOpacity: 1, // Stile semplificato
                outlineColor: '#000000', // Stile semplificato Nero
                outlineWidth: 3, // Stile semplificato
                closingPointColor: '#FAFAFA',
                closingPointWidth: 3,
                closingPointOutlineColor: '#000000', // Stile semplificato Nero
                closingPointOutlineWidth: 1
              }
            })
          }
        });
        map.addControl(newDrawControlInstance, 'top-right');
        drawControlRef.current = newDrawControlInstance; // Salva l'istanza nella ref
      }
    } else {
      // Se isActive è false o la mappa non è disponibile, e il controllo esiste, rimuovilo
      if (map && drawControlRef.current && map.hasControl(drawControlRef.current)) {
        try {
          map.removeControl(drawControlRef.current);
        } catch (e) {
          console.warn("Impossibile rimuovere il controllo draw (in else branch):", e);
        } finally {
          drawControlRef.current = null; // Resetta la ref
        }
      } else if (drawControlRef.current) {
        // Se la mappa non è disponibile ma la ref esiste, resettala comunque
        drawControlRef.current = null;
      }
    }

    // Funzione di cleanup per quando il componente viene smontato o le dipendenze cambiano
    return () => {
      if (map && drawControlRef.current && map.hasControl(drawControlRef.current)) {
        try {
          map.removeControl(drawControlRef.current);
        } catch (e) {
          console.warn("Impossibile rimuovere il controllo draw (cleanup):", e);
        } finally {
          drawControlRef.current = null; // Assicura che la ref sia resettata
        }
      } else if (drawControlRef.current) {
         // Se la mappa non è disponibile ma la ref esiste, resettala comunque
        drawControlRef.current = null;
      }
    };
  }, [map, isActive]);

  return null;
});

DrawControl.displayName = 'DrawControl';

export default DrawControl;


// USA QUESTO CODICE SE NON VUOI USARE IL CONTROLLO DI MISURA

// import { useEffect } from 'react';
// import { Map } from 'maplibre-gl';
// import { MaplibreTerradrawControl } from '@watergis/maplibre-gl-terradraw';
// import '@watergis/maplibre-gl-terradraw/dist/maplibre-gl-terradraw.css';
// import { TerraDrawPointMode, TerraDrawLineStringMode, TerraDrawPolygonMode } from 'terra-draw';

// interface MaplibreTerradrawControlProps {
//   map: Map | null;
//   isActive: boolean;
// }

// const DrawControl = ({ map, isActive }: MaplibreTerradrawControlProps) => {
//   useEffect(() => {
//     let drawControlInstance: MaplibreTerradrawControl | null = null;

//     if (map && isActive) {
//       // Verifica se un'istanza del controllo esiste già sulla mappa prima di aggiungerne una nuova.
//       // Questa è una semplificazione; una gestione più robusta potrebbe richiedere di tracciare l'istanza
//       // al di fuori di questo useEffect o di usare un riferimento.
//       // Per ora, assumiamo che se isActive è true, vogliamo il controllo.
//       // La logica di rimozione si occuperà di pulire.

//       drawControlInstance = new MaplibreTerradrawControl({
//         modes: ['render', 'select', 'delete-selection', 'delete', 'rectangle', 'angled-rectangle', 'polygon', 'linestring', 'circle', 'point', 'freehand', 'sensor', 'sector', 'download'],
//         open: false,
//         //computeElevation: true,
//         modeOptions: {
//           point: new TerraDrawPointMode({
//             styles: {
//               pointColor: '#0000FF', // Stile semplificato Blu
//               pointWidth: 5, // Stile semplificato
//               pointOutlineColor: '#0000FF', // Stile semplificato Blu
//               pointOutlineWidth: 1
//             }
//           }),
//           linestring: new TerraDrawLineStringMode({
//             styles: {
//               lineStringColor: '#00FF00', // Stile semplificato Verde
//               lineStringWidth: 5, // Stile semplificato
//               closingPointColor: '#FFFFFF',
//               closingPointWidth: 3,
//               closingPointOutlineColor: '#00FF00', // Stile semplificato Verde
//               closingPointOutlineWidth: 1
//             }
//           }),
//           polygon: new TerraDrawPolygonMode({
//             styles: {
//               fillColor: '#FFFF00', // Stile semplificato Giallo
//               fillOpacity: 1, // Stile semplificato
//               outlineColor: '#000000', // Stile semplificato Nero
//               outlineWidth: 3, // Stile semplificato
//               closingPointColor: '#FAFAFA',
//               closingPointWidth: 3,
//               closingPointOutlineColor: '#000000', // Stile semplificato Nero
//               closingPointOutlineWidth: 1
//             }
//           })
//         }
//       });
//       map.addControl(drawControlInstance, 'top-right');
//     }

//     return () => {
//       try {
//         // Rimuovi il controllo solo se è stato effettivamente aggiunto e la mappa esiste ancora
//         if (map && drawControlInstance && map.hasControl(drawControlInstance)) {
//           map.removeControl(drawControlInstance);
//         }
//       } catch (e) {
//         console.warn("Impossibile rimuovere il controllo draw:", e);
//       }
//     };
//   }, [map, isActive]);

//   return null;
// };

// export default DrawControl;